import { createContext, useContext, useEffect, useState } from 'react';
import { apiCall } from '../api/client.js';

const AuthContext = createContext(null);

const TOKEN_KEY = 'superhomes_access_token';
const USER_KEY = 'superhomes_user';

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(TOKEN_KEY);
  });
  const [user, setUser] = useState(() => {
    if (typeof window === 'undefined') return null;
    try { return JSON.parse(window.localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // Capture token from URL fragment (when a brand site redirects with #access=...)
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#access=')) return;
    const params = new URLSearchParams(hash.slice(1));
    const newToken = params.get('access');
    if (newToken) {
      window.localStorage.setItem(TOKEN_KEY, newToken);
      setAccessToken(newToken);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  // Hydrate user from /auth/me when token changes
  useEffect(() => {
    if (!accessToken) { setUser(null); return; }
    setLoading(true);
    apiCall('GET', '/api/v1/auth/me/', { token: accessToken })
      .then((res) => {
        const u = res?.data ?? res;
        window.localStorage.setItem(USER_KEY, JSON.stringify(u));
        setUser(u);
      })
      .catch(() => {
        window.localStorage.removeItem(TOKEN_KEY);
        window.localStorage.removeItem(USER_KEY);
        setAccessToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [accessToken]);

  const logout = () => {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(USER_KEY);
    setAccessToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ accessToken, user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
